<template>
  <div class="pa-3">
    <p class="caption grey--text">Company: {{ name }}</p>

    <v-layout wrap class="send-cols">
      <v-flex xs12 class="send-col">
        <v-card class="pa-4">
          <v-card-title primary-title>
            <div>
              <h3 class="headline mb-0">Gửi lệnh</h3>
              <div class="caption grey--text">Chọn thiết bị và gửi lệnh xuống</div>
            </div>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="isFormValid" lazy-validation>
              <v-layout wrap>
                <v-flex xs12>
                  <v-autocomplete
                    :items="vehicleOptions"
                    v-model="selectedVehicleId"
                    label="Tìm xe theo biển số"
                    item-text="label"
                    item-value="value"
                    dense
                    outlined
                    clearable
                    hide-no-data
                    no-data-text="Không tìm thấy xe"
                    :search-input.sync="vehicleQuery"
                    :filter="vehicleFilter"
                    prepend-inner-icon="search"
                    @change="onSelectVehicle"
                  >
                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center">
                        <div class="mr-2"><strong>{{ item.label }}</strong></div>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span>{{ item.label }}</span>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12>
                  <v-select
                    :items="deviceOptions"
                    v-model="selectedDevice"
                    label="Chọn thiết bị (IMEI)"
                    item-text="label"
                    item-value="value"
                    dense
                    outlined
                  />
                </v-flex>

                <v-flex xs12>
                  <v-text-field
                    v-model="messageCommand"
                    :rules="[rules.required]"
                    placeholder="Nhập lệnh..."
                    dense
                    outlined
                    clearable
                    validate-on-blur
                  />
                </v-flex>

                <v-flex xs12 class="text-right">
                  <v-btn color="primary" :disabled="!canSend" @click="sendCommand">Gửi lệnh</v-btn>
                </v-flex>
              </v-layout>
            </v-form>
            
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 class="send-col">
        <v-card class="pa-4 mt-4">
          <v-card-title primary-title>
            <div>
              <h3 class="headline mb-0">Lịch sử lệnh</h3>
              <div class="caption grey--text">Các lệnh đã gửi / trả lời</div>
            </div>
          </v-card-title>

          <v-card-text>
            <div v-if="commandInfo && commandInfo.length"
               class="command-history"
               @wheel.stop
               @touchmove.stop>
              <div v-for="(cmd, index) in commandInfo" :key="index" class="mb-2">
                <div>
                  <strong>Ngày:</strong> {{ formatGMTToLocalTime(cmd.createdDate) }}
                  &nbsp;—&nbsp;
                  <strong>Thiết bị trả lời:</strong> {{ cmd.isRequest ? 'Có' : 'Không' }}
                </div>
                <div><strong>Nội dung:</strong> {{ cmd.message }}</div>
                <v-divider class="my-2" />
              </div>
            </div>
            <div v-else class="text-center grey--text">
              Chưa có dữ liệu lịch sử cho thiết bị này.
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-snackbar v-model="snack.show" :timeout="2500" top right>
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { formatGMTToLocalTime } from '@/utils/datetime';
import validate from '@/utils/validate';

export default {
  name: 'SendCommand',
  props: {
    name: String,
    id: [String, Number],
    devices: Array,
    vehicles: {
      type: Array,
      default: () => []
    },
    selectedVehicle: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    selectedDevice: null,
    validate: validate,
    payload: '',
    snack: { show: false, text: '' },
    selectedVehicleId: null,
    vehicleQuery: '',
    commandInfo: null,
    messageCommand: '',
    isFormValid: true,
    rules: {
      required: v => (!!v && String(v).trim().length > 0) || 'Bắt buộc'
    }
  }),
  computed: {
    vehicleOptions () {
      const list = Array.isArray(this.vehicles) ? this.vehicles : []
      return list.map(v => ({
        value: v && v.Id != null ? String(v.Id) : null,
        label: v ? (v.Plate || v.LicensePlate || v.PlateNumber || v.Name || (`#${v.Id}`)) : 'Unknown',
        search: v ? (v.Plate || v.LicensePlate || v.PlateNumber || v.Name || '') : ''
      }))
    },

    deviceOptions () {
      const arr = Array.isArray(this.devices) ? this.devices : [];
      const vid = this.selectedVehicleId != null ? String(this.selectedVehicleId) : null;

      const filtered = arr.filter(d => {
        if (!vid) return true;
        const vIdCandidate =
          d && (d.VehicleId || (d.Vehicle && (d.Vehicle.Id || d.VehicleId)) || d.Vehicle) ;
        if (vIdCandidate == null) return false;
        return String(vIdCandidate) === vid;
      });

      return filtered.map(d => ({
        label: String((d && (d.Imei || d.Serial || d.Id)) || ''),
        value: d ? d.Id : null
      }));
    },

    canSend () {
      return !!(this.selectedDevice && this.messageCommand)
    }
  },

  watch: {
    selectedVehicle (nv) {
      this.selectedVehicleId = nv && nv.Id != null ? String(nv.Id) : null
    },

    selectedDevice: {
      immediate: false,
      handler (nv) {
        if (!nv) {
          this.commandInfo = null
          return
        }
        const device = Array.isArray(this.devices)
          ? this.devices.find(d => d && String(d.Id) === String(nv))
          : null
        const imei = device ? (device.Imei || device.Serial || device.Id) : null
        if (!imei) {
          this.commandInfo = null
          return
        }

        // hard-code imei for quick testing (you can replace with imei variable)
        const testImei = '013321063724'
        // call vuex action that returns a promise
        this.GetCommandByImei({ imei: testImei })
          .then(res => {
            this.commandInfo = res && res.data ? res.data : res
          })
          .catch(err => {
            this.commandInfo = null
            this.snack = { show: true, text: `Lấy info IMEI ${testImei} thất bại` }
          })
      }
    }
  },

  created () {
    if (this.selectedVehicle && this.selectedVehicle.Id != null) {
      this.selectedVehicleId = String(this.selectedVehicle.Id)
    }
  },

  methods: {
    formatGMTToLocalTime,
    ...mapActions(['GetCommandByImei', 'SendCommandToDevice']),

    async sendCommand () {
      try {
        const okForm = await this.$refs.form.validate();
        if (!okForm) return;

        const device = Array.isArray(this.devices)
          ? this.devices.find(d => d && String(d.Id) === String(this.selectedDevice))
          : null;
        const imei = device ? (device.Imei) : null;

        if (!imei) {
          this.snack = { show: true, text: 'Thiếu IMEI thiết bị' };
          return;
        }

        const data = {
          imei: String(imei),
          deviceId: (device && device.Id != null) ? device.Id : 0,
          vehicleId: (device && device.VehicleId != null) ? device.VehicleId : 0,
          deviceType: (device && device.DeviceType != null) ? device.DeviceType : 0,
          message: this.messageCommand
        };

        const responseSend = await this.SendCommandToDevice(data).catch(() => false);
        if (responseSend) {
          const res = await this.GetCommandByImei({ imei: String(imei) });
          this.commandInfo = res && res.data ? res.data : res;
          this.messageCommand = ''; 
          this.$nextTick(() => this.$refs.form.resetValidation());
          window.getApp.showMessage('Gửi lệnh thành công.', 'success');
        }
      } catch (e) {
        window.getApp.showMessage('Gửi lệnh thất bại.', 'error');
      }
    },

    _slug (s) {
      if (!s) return ''
      return String(s)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
        .toLowerCase()
    },

    vehicleFilter (item, queryText, itemText) {
      const q = this._slug(queryText)
      const hay = this._slug(item.search || itemText || '')
      return hay.includes(q)
    },

    onSelectVehicle (val) {
      const idStr = val != null ? String(val) : null
      const sel = idStr ? (Array.isArray(this.vehicles) ? this.vehicles.find(v => v && String(v.Id) === idStr) : null) : null
      this.$emit('vehicle-selected', { companyId: this.id, vehicle: sel })
      this.selectedDevice = null
    }
  }
}
</script>

<style scoped>
.device-details div { margin-bottom: 6px; }

/* small horizontal gutter between the two columns */
.send-cols {
  margin-left: -8px;
  margin-right: -8px;
}
.send-col {
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
}
@media (max-width: 959px) {
  .send-col { margin-bottom: 12px; }
}


.command-history {
  max-height: 50vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.command-history::-webkit-scrollbar {
  width: 6px;
}
.command-history::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

</style>



// WEBPACK FOOTER //
// src/pages/search/components/SendCommand.vue